import { ReactElement } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Details, Heading, Link, Paragraph, Skeleton, Table, Tag } from '@digdir/designsystemet-react';
import type { PlanCard } from '../../../api/safetyRounds';
import { Routes } from '../../../enums/Routes';
import { usePlansQuery } from '../../../hooks/queries/safetyRounds/usePlanQueries';
import { useStartRoundMutation } from '../../../hooks/queries/safetyRounds/useRoundQueries';
import { roundPath } from '../Round/roundView';
import { longDate, nextPlan, participantsText, planPath, roundActionOf, STATUS_COLORS } from './plansView';
import { protocolPath } from '../Protocol/protocolView';
import layout from '../../HRDashboard/hmsPage.module.css';
import classes from './Plans.module.css';

export const PlansPage = (): ReactElement => {
  const { t } = useTranslation();
  const { data, isLoading, error } = usePlansQuery();

  const content = () => {
    if (isLoading)
      return (
        <div aria-busy="true" className={layout.loading}>
          <span className="ds-sr-only">{t('safetyRounds.plans.loading')}</span>
          <Skeleton variant="rectangle" width="100%" height={360} />
        </div>
      );
    if (error || !data)
      return (
        <div className={layout.loading}>
          <Alert data-color="danger">
            <Heading level={2} data-size="2xs">
              {t('safetyRounds.errors.loadTitle')}
            </Heading>
            <Paragraph>{t('safetyRounds.errors.loadMessage')}</Paragraph>
          </Alert>
        </div>
      );
    const next = nextPlan(data.plans);
    return (
      <div className={classes.content}>
        {next ? <NextRound plan={next} /> : <EmptyPlans />}
        {data.plans.length > 0 && (
          <section aria-labelledby="plans-recurring" className={classes.section}>
            <div>
              <Heading level={2} data-size="sm" id="plans-recurring">
                {t('safetyRounds.plans.recurring')}
              </Heading>
              <Paragraph data-size="sm" className={layout.subtle}>
                {t('safetyRounds.plans.recurringIntro')}
              </Paragraph>
            </div>
            <ul className={classes.cards}>
              {data.plans.map((plan) => (
                <PlanCardItem key={plan.id} plan={plan} />
              ))}
            </ul>
          </section>
        )}
        <section aria-labelledby="plans-walked" className={classes.section}>
          <Heading level={2} data-size="sm" id="plans-walked">
            {t('safetyRounds.plans.walked')}
          </Heading>
          {data.rounds.length === 0 ? (
            <Paragraph data-size="sm" className={layout.subtle}>
              {t('safetyRounds.plans.noneWalked')}
            </Paragraph>
          ) : (
            <div className={layout.tableScroll}>
              <Table data-size="sm">
                <Table.Head>
                  <Table.Row>
                    <Table.HeaderCell>{t('safetyRounds.plans.table.round')}</Table.HeaderCell>
                    <Table.HeaderCell>{t('safetyRounds.plans.table.date')}</Table.HeaderCell>
                    <Table.HeaderCell>{t('safetyRounds.plans.table.deviations')}</Table.HeaderCell>
                    <Table.HeaderCell>{t('safetyRounds.plans.table.status')}</Table.HeaderCell>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {data.rounds.map((round) => (
                    <Table.Row key={round.id}>
                      <Table.Cell>
                        <Link asChild data-size="sm">
                          <RouterLink to={protocolPath(round.id)}>{round.planName}</RouterLink>
                        </Link>
                      </Table.Cell>
                      <Table.Cell>
                        <RoundDate day={round.completedOn ?? round.plannedDate} />
                      </Table.Cell>
                      <Table.Cell>{round.deviations}</Table.Cell>
                      <Table.Cell>{t(`safetyRounds.plans.roundStatus.${round.status}`)}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </section>
        {data.archived.length > 0 && (
          <Details data-size="sm">
            <Details.Summary>{t('safetyRounds.plans.archived', { count: data.archived.length })}</Details.Summary>
            <Details.Content>
              <ul className={classes.archived}>
                {data.archived.map((plan) => (
                  <li key={plan.id}>
                    <Link asChild data-size="sm">
                      <RouterLink to={planPath(plan.id)}>{plan.name}</RouterLink>
                    </Link>{' '}
                    · {plan.area}
                  </li>
                ))}
              </ul>
            </Details.Content>
          </Details>
        )}
      </div>
    );
  };

  return (
    <div className={layout.page} data-size="md">
      <section className={layout.hero}>
        <div aria-hidden="true" className={layout.backdrop} />
        <div className={`${layout.heroContent} ${classes.heroRow}`}>
          <div className={classes.heroText}>
            <span className={layout.eyebrow}>{t('safetyRounds.plans.eyebrow')}</span>
            <Heading level={1} data-size="xl">
              {t('safetyRounds.plans.title')}
            </Heading>
            <Paragraph className={layout.intro}>{t('safetyRounds.plans.intro')}</Paragraph>
          </div>
          <Button asChild data-size="sm">
            <RouterLink to={Routes.SafetyRepRoundPlanNew}>{t('safetyRounds.plans.new')}</RouterLink>
          </Button>
        </div>
      </section>
      {content()}
    </div>
  );
};

const RoundDate = ({ day }: { day: string }) => {
  const { i18n } = useTranslation();
  return <>{longDate(day, i18n.language)}</>;
};

const EmptyPlans = (): ReactElement => {
  const { t } = useTranslation();
  return (
    <section className={layout.card} data-gap="sm" aria-labelledby="plans-empty">
      <Heading level={2} data-size="sm" id="plans-empty">
        {t('safetyRounds.plans.emptyTitle')}
      </Heading>
      <Paragraph data-size="sm">{t('safetyRounds.plans.emptyText')}</Paragraph>
    </section>
  );
};

const NextRound = ({ plan }: { plan: PlanCard }): ReactElement => {
  const { t, i18n } = useTranslation();
  return (
    <section aria-labelledby="plans-next" className={`${layout.card} ${classes.next}`} data-gap="sm" data-highlight="true">
      <div className={classes.tags}>
        <span className={layout.eyebrow} id="plans-next">
          {t('safetyRounds.plans.next')}
        </span>
        <Tag data-color={STATUS_COLORS[plan.status]} data-size="sm">
          {t(`safetyRounds.plans.status.${plan.status}`)}
        </Tag>
      </div>
      <Heading level={2} data-size="md">
        {plan.name}
      </Heading>
      <Paragraph data-size="sm">
        {[longDate(plan.nextDate, i18n.language), plan.area, participantsText(plan.participants, t)].join(' · ')}
      </Paragraph>
      {(plan.mayConduct || plan.mayEdit) && (
        <div className={layout.actions}>
          {plan.mayConduct && <StartRound plan={plan} />}
          {plan.mayEdit && (
            <Button asChild data-size="sm" variant="secondary">
              <RouterLink to={planPath(plan.id)}>{t('safetyRounds.plans.editPlan')}</RouterLink>
            </Button>
          )}
        </div>
      )}
    </section>
  );
};

/** Starts the round of the plan, or opens the one already started. A list names the plan, so each button has its own name. */
export const StartRound = ({
  plan,
  variant = 'primary',
  name,
}: {
  plan: Pick<PlanCard, 'id' | 'currentRoundId' | 'status' | 'canStart' | 'startsFrom'>;
  variant?: 'primary' | 'secondary';
  name?: string;
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const start = useStartRoundMutation();
  const label = (action: string) => (name ? `${action}: ${name}` : undefined);
  const action = roundActionOf(plan);
  if (action.kind === 'notYet')
    return (
      <Paragraph data-size="sm" className={layout.subtle}>
        {t('safetyRounds.round.startsFrom', { date: longDate(action.startsFrom, i18n.language) })}
      </Paragraph>
    );
  if (action.kind !== 'start') {
    const [to, text] =
      action.kind === 'protocol'
        ? [protocolPath(action.roundId), t('safetyRounds.round.seeProtocol')]
        : action.kind === 'correct'
          ? [protocolPath(action.roundId), t('safetyRounds.round.correct')]
          : [roundPath(action.roundId), t('safetyRounds.round.continue')];
    return (
      <Button asChild data-size="sm" variant={variant}>
        <RouterLink to={to} aria-label={label(text)}>
          {text}
        </RouterLink>
      </Button>
    );
  }
  return (
    <>
      <Button
        data-size="sm"
        variant={variant}
        aria-label={label(t('safetyRounds.round.start'))}
        aria-busy={start.isPending || undefined}
        onClick={() => start.mutate(plan.id, { onSuccess: ({ id }) => navigate(roundPath(id)) })}
      >
        {t('safetyRounds.round.start')}
      </Button>
      {start.isError && (
        <Alert data-color="danger" data-size="sm">
          <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
        </Alert>
      )}
    </>
  );
};

const PlanCardItem = ({ plan }: { plan: PlanCard }): ReactElement => {
  const { t, i18n } = useTranslation();
  return (
    <li className={classes.card}>
      <div className={classes.tags}>
        <Tag data-color={STATUS_COLORS[plan.status]} data-size="sm">
          {t(`safetyRounds.plans.status.${plan.status}`)}
        </Tag>
        <Tag data-color="neutral" data-size="sm">
          {t(`safetyRounds.plans.frequency.${plan.frequency}`)}
        </Tag>
      </div>
      <div>
        <Heading level={3} data-size="xs">
          {plan.name}
        </Heading>
        <Paragraph data-size="sm" className={layout.subtle}>
          {plan.area}
        </Paragraph>
      </div>
      <dl className={classes.facts}>
        <div>
          <dt>{t('safetyRounds.plans.nextDate')}</dt>
          <dd>{longDate(plan.nextDate, i18n.language)}</dd>
        </div>
        <div>
          <dt>{t('safetyRounds.plans.lastCompleted')}</dt>
          <dd>{plan.lastCompletedOn ? longDate(plan.lastCompletedOn, i18n.language) : t('safetyRounds.plans.notYet')}</dd>
        </div>
        <div>
          <dt>{t('safetyRounds.plans.checklist')}</dt>
          <dd>
            {plan.template
              ? t('safetyRounds.plans.checklistVersion', { name: plan.template.name, version: plan.template.version })
              : '–'}
          </dd>
        </div>
        <div>
          <dt>{t('safetyRounds.plans.openDeviations')}</dt>
          <dd>{plan.openDeviations}</dd>
        </div>
      </dl>
      {plan.mayConduct && (
        <div>
          <StartRound plan={plan} variant="secondary" />
        </div>
      )}
      {plan.mayEdit && (
        <div>
          <Link asChild data-size="sm">
            <RouterLink to={planPath(plan.id)}>{t('safetyRounds.plans.editNamed', { name: plan.name })}</RouterLink>
          </Link>
        </div>
      )}
    </li>
  );
};
